<template>
  <div
    class="carrier-portal-document-list-item"
    :class="status"
    role="group"
  >
    <a
      v-if="doc && !loading"
      :href="doc.signedUrl"
      :download="doc.documentName"
      class="carrier-portal-document-list-item-content carrier-portal-document-download-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      <CarrierPortalDocumentListItemMeta
        :doc="doc"
        :type="type"
        :status="status"
      />
    </a>
    <div v-else class="carrier-portal-document-list-item-content">
      <CarrierPortalDocumentListItemMeta
        :doc="doc"
        :type="type"
        :status="status"
      />
    </div>

    <div class="button-container">
      <template v-if="doc">
        <RygenButton
          mode="tertiary"
          class="carrier-portal-document-list-item-button"
          icon="pi pi-download"
          :aria-label="t('common.downloadParam', { param: documentTypeText })"
          :loading="loading"
          @click.stop="onView"
        />
        <RygenButton
          mode="tertiary"
          class="carrier-portal-document-list-item-button"
          icon="pi pi-times"
          :aria-label="t('common.removeParam', { param: documentTypeText })"
          :loading="loading"
          @click.stop="emit('remove', doc)"
        />
      </template>
      <RygenButton
        v-else-if="error"
        mode="tertiary"
        class="carrier-portal-document-list-item-button"
        icon="pi pi-replay"
        :aria-label="t('common.retry')"
        :severity="Severity.WARNING"
        :loading="loading"
        @click.stop="emit('retry', type)"
      />
      <RygenButton
        v-else
        mode="tertiary"
        class="carrier-portal-document-list-item-button"
        :label="t('upload.upload')"
        :aria-label="t('common.uploadParam', { param: documentTypeText })"
        :loading="loading"
        @click.stop="emit('upload', type)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateEnum } from '@rygen/rygen-ui/i18n';
import { CorsairDocumentType, Severity } from '@rygen/rygen-ui/model';

import { DocumentUploadStatus, SignedDocument } from '@/model';

import CarrierPortalDocumentListItemMeta from '@/components/carrierPortal/document/CarrierPortalDocumentListItemMeta.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';

const props = defineProps({
  doc: {
    type: Object as PropType<SignedDocument>,
    default: () => undefined,
  },
  type: {
    type: String as PropType<CorsairDocumentType>,
    default: () => CorsairDocumentType.BOL,
  },
  error: {
    type: Object as PropType<any>,
    default: () => undefined,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
});
const { doc, type, error, loading, required } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', doc: SignedDocument): void;
  (event: 'retry', type: CorsairDocumentType): void;
  (event: 'upload', type: CorsairDocumentType): void;
}>();

const { t } = useI18n();

const status = computed((): DocumentUploadStatus => {
  if (doc?.value) {
    return 'present';
  } else if (required.value) {
    return 'not-present';
  } else if (error?.value) {
    return 'error';
  } else {
    return 'placeholder';
  }
});

const documentTypeText = computed(() =>
  translateEnum(type.value, 'CorsairDocumentType'),
);

const onView = () => {
  if (doc?.value?.signedUrl) {
    window.open(doc.value.signedUrl, '_blank', 'noopener,noreferrer');
  }
};
</script>

<style>
.carrier-portal-document-list-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--sp-4);
  padding: var(--sp-4);
  background: transparent;

  .carrier-portal-document-list-item-content {
    flex: 0 1 auto;
    min-width: 0;

    &.carrier-portal-document-download-link {
      display: inline-block;
      text-decoration: none;
      color: inherit;
    }
  }

  .button-container {
    display: flex;
    align-items: center;
    gap: var(--sp-1);
    flex: 0 0 auto;
  }

  &.not-present {
    &::before {
      content: '';
      position: absolute;
      top: var(--sp-3);
      bottom: var(--sp-3);
      left: 0;
      width: 4px;
      background: var(--danger-color);
      border-radius: 2px;
    }

    .carrier-portal-document-list-item-file-icon {
      color: var(--danger-color);
    }

    .carrier-portal-document-list-item-meta-missing {
      color: var(--danger-color);
    }
  }

  &.present {
    .carrier-portal-document-list-item-file-icon {
      color: var(--p-gray-700);
    }
  }

  &.error {
    .carrier-portal-document-list-item-file-icon {
      color: var(--warning-color);
    }
  }
}
</style>
